import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Download, FileSpreadsheet, Calendar, Building2, Coins } from 'lucide-react';
import { format } from 'date-fns';

const ExportPage = () => {
  const { api } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [selectedStores, setSelectedStores] = useState([]);
  const [dateFrom, setDateFrom] = useState(format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [loading, setLoading] = useState(true);
  const [loadingStores, setLoadingStores] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState('detailed');

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    if (selectedBusiness) {
      fetchStores(selectedBusiness);
    } else {
      setStores([]);
      setSelectedStores([]);
    }
  }, [selectedBusiness]);

  const fetchBusinesses = async () => {
    try {
      const res = await api.get('/api-keys/all');
      setBusinesses(res.data);
      if (res.data.length > 0) {
        setSelectedBusiness(res.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load businesses');
    } finally {
      setLoading(false);
    }
  };

  const fetchStores = async (businessId) => {
    setLoadingStores(true);
    try {
      const res = await api.get(`/stores/${businessId}`);
      setStores(res.data);
      // Select all stores by default
      setSelectedStores(res.data.map(s => s.store_name));
    } catch (error) {
      console.error('Failed to load stores');
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  };

  const handleStoreToggle = (storeName) => {
    setSelectedStores(prev => {
      if (prev.includes(storeName)) {
        return prev.filter(s => s !== storeName);
      } else {
        return [...prev, storeName];
      }
    });
  };

  const handleSelectAllStores = () => {
    if (selectedStores.length === stores.length) {
      setSelectedStores([]);
    } else {
      setSelectedStores(stores.map(s => s.store_name));
    }
  };

  const handleExport = async (type) => {
    if (!selectedBusiness) {
      toast.error('Please select a business');
      return;
    }

    if (selectedStores.length === 0) {
      toast.error('Please select at least one branch');
      return;
    }

    setExporting(true);
    try {
      const endpoint = type === 'tips' ? '/export/tips' : '/export/detailed';
      const response = await api.get(endpoint, {
        params: {
          business_id: selectedBusiness,
          date_from: dateFrom,
          date_to: dateTo,
          stores: selectedStores.join(',')
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const business = businesses.find(b => b.id === selectedBusiness);
      const exportLabel = type === 'tips' ? 'tips' : 'detailed';
      const filename = `${business?.business_name.replace(/\s+/g, '_')}_${exportLabel}_${dateFrom}_to_${dateTo}.xlsx`;
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Skeleton className="h-10 w-64 mb-6" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="page-container animate-slide-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1A1A18]">Export Data</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Export shift, ledger, and tips data to Excel
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Selection Panel */}
        <Card data-testid="export-selection">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#006241]" />
              Select Data
            </CardTitle>
            <CardDescription>
              Choose business, branches, and date range
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Business</Label>
              <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                <SelectTrigger data-testid="export-business-select">
                  <SelectValue placeholder="Select business" />
                </SelectTrigger>
                <SelectContent>
                  {businesses.map(b => (
                    <SelectItem key={b.id} value={b.id}>{b.business_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Branches</Label>
                {stores.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAllStores}
                    className="text-xs h-6"
                  >
                    {selectedStores.length === stores.length ? 'Deselect All' : 'Select All'}
                  </Button>
                )}
              </div>
              {loadingStores ? (
                <Skeleton className="h-20" />
              ) : stores.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">No branches found</p>
              ) : (
                <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                  {stores.map((store) => (
                    <div key={store.store_name} className="flex items-center gap-2">
                      <Checkbox
                        id={`store-${store.store_name}`}
                        checked={selectedStores.includes(store.store_name)}
                        onCheckedChange={() => handleStoreToggle(store.store_name)}
                      />
                      <label
                        htmlFor={`store-${store.store_name}`}
                        className="text-sm cursor-pointer flex-1"
                      >
                        {store.store_name}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {selectedStores.length} of {stores.length} branch{stores.length !== 1 ? 'es' : ''} selected
                {selectedStores.length > 1 && ' (data will be broken down per branch)'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  From Date
                </Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="export-date-from"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  To Date
                </Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="export-date-to"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export Options */}
        <div className="space-y-4">
          {/* Detailed Export */}
          <Card data-testid="detailed-export">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSpreadsheet className="h-5 w-5 text-[#006241]" />
                Detailed Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F2F2F0] p-3 rounded-lg">
                <p className="text-sm font-medium mb-2">Includes:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Shifts with all payment details</li>
                  <li>• Cashup data (banked cash, over/under, tips)</li>
                  <li>• Ledger entries for the period</li>
                  <li>• Staff member assignments</li>
                </ul>
              </div>
              <Button 
                onClick={() => handleExport('detailed')}
                disabled={exporting || !selectedBusiness || selectedStores.length === 0}
                className="w-full h-10 bg-[#006241] hover:bg-[#004d33]"
                data-testid="detailed-export-btn"
              >
                {exporting ? (
                  <>Exporting...</>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Detailed Export
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Tips Export */}
          <Card data-testid="tips-export">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Coins className="h-5 w-5 text-[#006241]" />
                Tips Export
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F2F2F0] p-3 rounded-lg">
                <p className="text-sm font-medium mb-2">Format:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Date</li>
                  <li>• Cash Tips (recognised)</li>
                  <li>• Card Tips (recognised)</li>
                  <li>• Total Tips</li>
                </ul>
                <p className="text-xs text-muted-foreground mt-2 italic">
                  Only includes reconciled shifts
                </p>
              </div>
              <Button 
                onClick={() => handleExport('tips')}
                disabled={exporting || !selectedBusiness || selectedStores.length === 0}
                className="w-full h-10 bg-[#006241] hover:bg-[#004d33]"
                data-testid="tips-export-btn"
              >
                {exporting ? (
                  <>Exporting...</>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Tips Export
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ExportPage;
